/**
 * „Skopiuj na…" — one day's windows onto the days the trainer ticks (mobile's copy-to-weekdays).
 *
 * The copy REPLACES the ticked days' windows, and it only edits the week in hand: nothing is saved
 * until „Zapisz", which the body says. Every other day starts ticked, since the commonest copy is
 * „the same every day"; the confirm stays focusable while nothing is ticked, and says why. The
 * dialog opens on the first day's box.
 */

import { Dialog } from '@base-ui/react/dialog';
import { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import surface from '~/shared/ui/dialogSurface.module.css';
import form from '~/shared/ui/formDialog.module.css';
import { CloseGlyph } from '~/shared/ui/FormGlyphs';

import { WEEKDAYS, type Weekday } from '../model/weekPattern';
import styles from './ScheduleTab.module.css';

interface CopyDayDialogProps {
  readonly from: Weekday;
  readonly onCopy: (onto: readonly Weekday[]) => void;
  readonly onClose: () => void;
}

export function CopyDayDialog({ from, onCopy, onClose }: CopyDayDialogProps) {
  const { t } = useTranslation();
  const reasonId = useId();
  const [onto, setOnto] = useState<readonly Weekday[]>(WEEKDAYS.filter((day) => day !== from));
  const none = onto.length === 0;
  const firstDay = useRef<HTMLInputElement>(null);
  const days = WEEKDAYS.filter((day) => day !== from);

  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Backdrop className={surface.backdrop} />
        <Dialog.Popup
          className={`${String(surface.popup)} ${String(form.question)}`}
          initialFocus={firstDay}
        >
          <div className={form.dialogHead}>
            <Dialog.Title className={form.dialogTitle}>
              {t('web:booking.schedule.copyDayTitle', {
                day: t(`web:weekdays.accusative.${String(from)}`),
              })}
            </Dialog.Title>
            <Dialog.Close className={form.close} aria-label={t('web:booking.schedule.close')}>
              <CloseGlyph className={form.closeGlyph} />
            </Dialog.Close>
          </div>
          <Dialog.Description className={form.questionBody}>
            {t('web:booking.schedule.copyDayBody')}
          </Dialog.Description>
          <fieldset className={styles.copyDays}>
            <legend className={form.fieldLabel}>{t('web:booking.schedule.copyDayDays')}</legend>
            {days.map((day) => (
              <label key={day} className={styles.copyDay}>
                <input
                  ref={day === days[0] ? firstDay : undefined}
                  type="checkbox"
                  checked={onto.includes(day)}
                  onChange={(event) => {
                    setOnto(
                      event.target.checked
                        ? WEEKDAYS.filter((entry) => entry === day || onto.includes(entry))
                        : onto.filter((entry) => entry !== day),
                    );
                  }}
                />
                {t(`web:weekdays.full.${String(day)}`)}
              </label>
            ))}
          </fieldset>
          <div className={form.dialogActs}>
            <button type="button" className={surface.ghost} onClick={onClose}>
              {t('web:booking.schedule.cancel')}
            </button>
            {none ? (
              <span id={reasonId} className={form.actReason}>
                {t('web:booking.schedule.copyDayNone')}
              </span>
            ) : null}
            <button
              type="button"
              className={surface.primary}
              aria-disabled={none ? true : undefined}
              aria-describedby={none ? reasonId : undefined}
              onClick={() => {
                if (!none) onCopy(onto);
              }}
            >
              {t('web:booking.schedule.copyDayConfirm')}
            </button>
          </div>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
